import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { roleFormSchema, RoleFormSchemaValues } from 'src/features/roles/utils/formSchema';
import { Role } from 'src/features/roles/utils/types';
import { useGetRole } from 'src/features/roles/api/useGetRole';
import RoleForm from 'src/features/roles/components/forms/RoleForm';
import { GeneralASError } from 'src/registry/types/axios';

interface RoleFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedRole?: Role;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (data: RoleFormSchemaValues, onSuccess: () => void, onError: (error: GeneralASError) => void) => void;
}

function RoleFormModal<TData>({
    selectedRole,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: RoleFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: role } = useGetRole({
        roleId: selectedRole?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedRole?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            role
                ? { ...role, permissions: undefined }
                : {
                      name: '',
                      permissions: [],
                  },
        [role],
    );

    const form = useForm<RoleFormSchemaValues>({
        resolver: zodResolver(roleFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [role, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: RoleFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, form, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <RoleForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedRole && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default RoleFormModal;
